import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../components/ui/Toast';
import {
  useCampaign,
  useCampaignSequence,
  useSaveCampaignSequence,
  useUpdateCampaign,
  type SequenceStep,
  type TriggerConfig,
  type TriggerKind,
} from '../../api/campaigns.api';
import { useTemplates, type Template } from '../../api/templates.api';
import { useAllAvailableLists } from '../../api/audiences.api';
import { EMPLOYMENT_TYPE_OPTIONS, placementOptionValue } from '../../lib/trigger-options';
import { MeetingBookedFields } from './MeetingBookedFields';
import { COMMON_MERGE_TOKENS, TASK_MERGE_TOKENS } from '../../lib/merge-tokens';
import { useOrgUsers } from '../../api/org-users.api';
import { TaskAssigneeSelect, TokenField } from './step-fields';
import { useLinkiDraft, useLinkiRewrite } from '../../api/linki.api';

/* ── Screens 18–19 — Workflow builder (visual canvas) ──────────────────────
 *
 * The canvas reads & writes the same /campaigns/:id/sequence payload that the
 * wizard's CampaignSequencePage uses. Two views, one persisted model.
 *
 * Branching: kept visually flat for v1. A BRANCH step renders as a regular
 * node in the linear chain; its `branch_condition` JSON encodes the routing
 * (condition + yes/no target step_number). This matches the backend's flat
 * `sequence.steps` array — the nested yes/no canvas the concept mocked is a
 * future enhancement once the schema supports tree edges natively.
 *
 * Read-only when the campaign isn't a DRAFT: the API rejects edits anyway
 * (sequences.service.ts), so we mirror that by disabling Save + form fields.
 */

type StepType = SequenceStep['type'];

const MAX_STEPS = 7;

const STEP_TYPE_LABEL: Record<StepType, string> = {
  EMAIL: 'Email',
  SMS:   'SMS',
  DELAY: 'Wait',
  BRANCH: 'Branch',
  TASK: 'Task',
  EXIT: 'Exit',
};

interface TypeAccent { bg: string; fg: string; border: string }

const TYPE_ACCENTS: Record<StepType, TypeAccent> = {
  EMAIL:  { bg: 'var(--color-blue-50)',       fg: 'var(--color-brand-blue)',   border: 'var(--color-blue-100)' },
  SMS:    { bg: 'var(--color-indigo-50)',     fg: 'var(--color-brand-indigo)', border: 'var(--color-indigo-100)' },
  DELAY:  { bg: 'var(--color-surface-muted)', fg: 'var(--color-text-muted)',   border: 'var(--color-border-default)' },
  BRANCH: { bg: 'var(--color-indigo-50)',     fg: 'var(--color-brand-indigo)', border: 'var(--color-indigo-100)' },
  TASK:   { bg: 'var(--color-warning-bg)',    fg: 'var(--color-warning)',      border: 'var(--color-warning-border)' },
  EXIT:   { bg: 'var(--color-navy-50)',       fg: 'var(--color-brand-navy)',   border: 'var(--color-navy-100)' },
};

// These mirror the workflow engine's branch_condition.type union — keep in
// sync with workflow-engine.service.ts. UNSUBSCRIBED isn't supported as a
// branch yet (it ends the sequence directly), so it's omitted here.
const BRANCH_CONDITIONS = [
  { value: 'replied', label: 'Replied' },
  { value: 'opened', label: 'Opened' },
  { value: 'clicked', label: 'Clicked' },
] as const;

/** Stored shape — matches workflow-engine.service.ts SequenceStepJson.branch_condition. */
interface BranchCondition {
  type?: 'replied' | 'opened' | 'clicked';
  on_true?: number | null;
  on_false?: number | null;
}

export function WorkflowBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: campaign, isLoading: loadingCampaign } = useCampaign(id);
  const { data: existing, isLoading: loadingSeq } = useCampaignSequence(id);
  const { data: templatesResp } = useTemplates();
  const save = useSaveCampaignSequence(id ?? '');
  const updateCampaign = useUpdateCampaign(id ?? '');

  const [steps, setSteps] = useState<SequenceStep[]>([]);
  // 'trigger' selects the auto-enrollment config; numeric selects a step.
  const [selection, setSelection] = useState<'trigger' | number | null>(null);
  const [zoom, setZoom] = useState(100);
  const [dirty, setDirty] = useState(false);

  // Hydrate when the sequence loads. Only resets local state if we haven't
  // already started editing — protects against the user clicking Save and
  // having their unsaved tweaks blown away by the refetch.
  useEffect(() => {
    if (dirty) return;
    if (existing?.steps && existing.steps.length > 0) {
      setSteps(existing.steps as SequenceStep[]);
      setSelection(0);
    } else if (existing === null && !loadingSeq && steps.length === 0) {
      // Empty draft → seed with one EMAIL step so the canvas isn't blank.
      setSteps([{ step_number: 1, type: 'EMAIL', subject: '', body_text: '' }]);
      setSelection(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing, loadingSeq]);

  if (!id) return <Navigate to="/campaigns" replace />;
  if (loadingCampaign) return <LoadingShell />;
  if (!campaign) return <Navigate to="/campaigns" replace />;

  const templates = templatesResp?.data ?? [];
  const isDraft = campaign.status === 'DRAFT';
  const selectedStepIdx = typeof selection === 'number' ? selection : null;
  const selectedStep = selectedStepIdx != null ? steps[selectedStepIdx] ?? null : null;
  const triggerSelected = selection === 'trigger';

  const updateStep = (idx: number, patch: Partial<SequenceStep>) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    setDirty(true);
  };

  const addStep = (type: StepType) => {
    if (steps.length >= MAX_STEPS) return;
    setSteps((prev) => {
      const next = [
        ...prev,
        {
          step_number: prev.length + 1,
          type,
          ...(type === 'DELAY' ? { delay_days: 2 } : {}),
          ...(type === 'EMAIL' ? { subject: '', body_text: '' } : {}),
          ...(type === 'TASK' ? { task_title: '', task_due_days: 2 } : {}),
        } as SequenceStep,
      ];
      setSelection(next.length - 1);
      return next;
    });
    setDirty(true);
  };

  const removeStep = (idx: number) => {
    setSteps((prev) => {
      const next = prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 }));
      setSelection(next.length === 0 ? null : Math.min(idx, next.length - 1));
      return next;
    });
    setDirty(true);
  };

  // Trigger config writes go straight to the API — they're a single field on
  // the campaign, so there's no "stage local then save" benefit. The PATCH
  // also invalidates the campaign query so the trigger node re-renders.
  const saveTrigger = async (config: TriggerConfig | null) => {
    try {
      await updateCampaign.mutateAsync({ trigger_config: config });
      addToast({
        variant: 'success',
        title: config ? 'Trigger updated' : 'Trigger cleared',
      });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Try again in a moment.';
      addToast({ variant: 'danger', title: 'Could not save trigger', description: msg });
    }
  };

  const validate = (): string | null => {
    if (steps.length === 0) return 'Add at least one step.';
    for (const s of steps) {
      if (s.type === 'EMAIL' && !(s.subject ?? '').trim()) {
        return `Step ${s.step_number}: an email step needs a subject line.`;
      }
      if (s.type === 'EMAIL' && !(s.body_text ?? '').trim() && !s.template_id) {
        return `Step ${s.step_number}: pick a template or write a body.`;
      }
      if (s.type === 'DELAY' && (s.delay_days == null || s.delay_days < 0)) {
        return `Step ${s.step_number}: delay days must be 0 or more.`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      addToast({ variant: 'warning', title: 'Cannot save', description: error });
      return;
    }
    try {
      await save.mutateAsync({ steps });
      addToast({ variant: 'success', title: 'Workflow saved' });
      setDirty(false);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Try again in a moment.';
      addToast({ variant: 'danger', title: 'Save failed', description: msg });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <Breadcrumb campaignId={id} campaignName={campaign.name} />

      <HeaderRow
        campaignName={campaign.name}
        statusBadge={isDraft ? null : `${campaign.status.toLowerCase()} (read-only)`}
        onSave={handleSave}
        saving={save.isPending}
        disabled={!isDraft || !dirty}
      />

      <Toolbar
        isEmpty={steps.length === 0}
        zoom={zoom}
        canAdd={isDraft && steps.length < MAX_STEPS}
        onZoomIn={() => setZoom((z) => Math.min(200, z + 10))}
        onZoomOut={() => setZoom((z) => Math.max(50, z - 10))}
        onZoomFit={() => setZoom(100)}
        onAddStep={addStep}
        onBackToDetail={() => navigate(`/campaigns/${id}`)}
      />

      <div style={{ display: 'grid', gridTemplateColumns: selection != null ? 'minmax(0, 1fr) 360px' : 'minmax(0, 1fr)', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
        <Canvas zoom={zoom}>
          <TriggerNode
            campaignName={campaign.name}
            audience={campaign.audience}
            triggerConfig={campaign.trigger_config ?? null}
            selected={triggerSelected}
            onSelect={() => setSelection('trigger')}
          />
          {steps.length === 0 ? (
            <EmptyPrompt onAdd={addStep} disabled={!isDraft} />
          ) : (
            <FlowRenderer steps={steps} selectedIdx={selectedStepIdx} onSelect={(i) => setSelection(i)} />
          )}
        </Canvas>

        {triggerSelected && (
          <TriggerConfigPanel
            triggerConfig={campaign.trigger_config ?? null}
            readOnly={!isDraft}
            saving={updateCampaign.isPending}
            onSave={saveTrigger}
            onClose={() => setSelection(null)}
          />
        )}

        {selectedStep && (
          <ConfigPanel
            step={selectedStep}
            stepIdx={selectedStepIdx!}
            allSteps={steps}
            templates={templates}
            readOnly={!isDraft}
            onChange={(patch) => updateStep(selectedStepIdx!, patch)}
            onRemove={() => removeStep(selectedStepIdx!)}
            onClose={() => setSelection(null)}
          />
        )}
      </div>
    </div>
  );
}

/* ── Breadcrumb ── */

function Breadcrumb({ campaignId, campaignName }: { campaignId: string; campaignName: string }) {
  const items = [
    { label: 'Engage', to: '/dashboard' },
    { label: 'Campaigns', to: '/campaigns' },
    { label: campaignName, to: `/campaigns/${campaignId}` },
    { label: 'Workflow', to: null as string | null },
  ];
  return (
    <nav aria-label="Breadcrumb">
      <ol style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0, flexWrap: 'wrap' }}>
        {items.map((it, i) => (
          <li key={it.label + i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
            {it.to ? (
              <Link to={it.to} style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textDecoration: 'none' }}>
                {it.label}
              </Link>
            ) : (
              <span aria-current="page" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>
                {it.label}
              </span>
            )}
            {i < items.length - 1 && <span aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}><IconChevronRight size={12} /></span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ── Header ── */

function HeaderRow({
  campaignName,
  statusBadge,
  onSave,
  saving,
  disabled,
}: {
  campaignName: string;
  statusBadge: string | null;
  onSave: () => void;
  saving: boolean;
  disabled: boolean;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <div>
        <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)', lineHeight: 'var(--leading-tight)' }}>
          Workflow · {campaignName}
        </h1>
        <p style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-muted)' }}>
          {statusBadge
            ? `Campaign is ${statusBadge}. The sequence is locked until paused.`
            : 'Steps run in order. Replies and bounces automatically exit the sequence.'}
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <PrimaryButton onClick={onSave} disabled={disabled || saving}>
          <IconSave size={14} />
          <span style={{ marginLeft: 'var(--space-1)' }}>{saving ? 'Saving…' : 'Save changes'}</span>
        </PrimaryButton>
      </div>
    </div>
  );
}

/* ── Toolbar ── */

function Toolbar({
  isEmpty,
  zoom,
  canAdd,
  onZoomIn,
  onZoomOut,
  onZoomFit,
  onAddStep,
  onBackToDetail,
}: {
  isEmpty: boolean;
  zoom: number;
  canAdd: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomFit: () => void;
  onAddStep: (t: StepType) => void;
  onBackToDetail: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-2) var(--space-3)',
        backgroundColor: 'var(--color-surface-white)',
        border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-input)',
        boxShadow: 'var(--shadow-xs)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
        <ToolbarButton icon={<IconMail size={12} />} onClick={() => onAddStep('EMAIL')} disabled={!canAdd}>Email</ToolbarButton>
        <ToolbarButton icon={<IconClock size={12} />} onClick={() => onAddStep('DELAY')} disabled={!canAdd}>Wait</ToolbarButton>
        <ToolbarButton icon={<IconBranch size={12} />} onClick={() => onAddStep('BRANCH')} disabled={!canAdd}>Branch</ToolbarButton>
        <ToolbarButton icon={<IconBell size={12} />} onClick={() => onAddStep('TASK')} disabled={!canAdd}>Task</ToolbarButton>
        <ToolbarButton icon={<IconSquare size={12} />} onClick={() => onAddStep('EXIT')} disabled={!canAdd}>Exit</ToolbarButton>
        <ToolbarSep />
        <ToolbarButton onClick={onBackToDetail}>Back to campaign</ToolbarButton>
        {isEmpty && !canAdd && (
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginLeft: 'var(--space-2)' }}>
            Sequence is locked while the campaign is non-draft.
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-muted)',
            minWidth: '40px',
            textAlign: 'center',
          }}
        >
          {zoom}%
        </span>
        <ZoomButton aria-label="Zoom out" onClick={onZoomOut}><IconMinus size={12} /></ZoomButton>
        <ZoomButton aria-label="Zoom in" onClick={onZoomIn}><IconPlus size={12} /></ZoomButton>
        <ToolbarButton onClick={onZoomFit}>Fit</ToolbarButton>
      </div>
    </div>
  );
}

function ToolbarButton({ icon, children, onClick, disabled }: { icon?: ReactNode; children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        height: 'var(--btn-height-sm)',
        padding: '0 var(--btn-padding-x-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'transparent',
        border: 'none',
        color: disabled ? 'var(--color-text-subtle)' : 'var(--color-text-secondary)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color var(--transition-base), color var(--transition-base)',
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
        e.currentTarget.style.color = 'var(--color-text-primary)';
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = 'var(--color-text-secondary)';
      }}
    >
      {icon}
      {children}
    </button>
  );
}

function ZoomButton({ children, onClick, 'aria-label': ariaLabel }: { children: ReactNode; onClick?: () => void; 'aria-label': string }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'var(--btn-height-sm)',
        height: 'var(--btn-height-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'transparent',
        border: '1px solid var(--color-border-default)',
        color: 'var(--color-text-muted)',
        cursor: 'pointer',
        transition: 'background-color var(--transition-base)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
    >
      {children}
    </button>
  );
}

function ToolbarSep() {
  return <span aria-hidden="true" style={{ width: '1px', height: '20px', backgroundColor: 'var(--color-border-default)', margin: '0 var(--space-1)' }} />;
}

/* ── Canvas ── */

function Canvas({ zoom, children }: { zoom: number; children: ReactNode }) {
  return (
    <div
      style={{
        position: 'relative',
        minHeight: '720px',
        backgroundColor: 'var(--workflow-canvas-bg)',
        backgroundImage: 'radial-gradient(circle, var(--workflow-canvas-dot-color) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
        border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-card)',
        boxShadow: 'var(--shadow-xs)',
        overflow: 'auto',
        padding: 'var(--space-8)',
      }}
    >
      <div
        style={{
          transform: `scale(${zoom / 100})`,
          transformOrigin: 'top center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
          transition: 'transform var(--transition-base)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/* ── Trigger node ── */

const TRIGGER_KIND_LABEL: Record<TriggerKind, string> = {
  application_stage_change: 'Job pipeline stage',
  deal_stage_change: 'Deal pipeline stage',
  application_stage_dwell: 'Job stage dwell',
  deal_stage_dwell: 'Deal stage dwell',
  meeting_booked: 'Meeting booked',
  stage_change: 'Stage change',
  list_added: 'Added to list',
  new_placement: 'New placement',
};

function describeTrigger(config: TriggerConfig | null): string {
  if (!config) return 'Static audience snapshot';
  const match = config.match ?? {};
  switch (config.type) {
    case 'application_stage_change': {
      const to = match.to_stage ? `→ ${match.to_stage}` : '';
      const from = match.from_stage ? `${match.from_stage} ` : '';
      const scope = match.job_id ? ' (one job)' : '';
      return `Job stage ${from}${to}`.trim() + scope || 'Any application stage change';
    }
    case 'deal_stage_change':
      return match.to_stage_id ? 'Deal moved into stage' : 'Deal stage change';
    case 'application_stage_dwell':
      return `In ${match.stage ?? 'stage'} for ${match.dwell_days ?? '?'} days`;
    case 'deal_stage_dwell':
      return `Deal in stage for ${match.dwell_days ?? '?'} days`;
    case 'meeting_booked': {
      const links = match.event_type_ids?.length ?? 0;
      const where = links ? ` on ${links} booking link${links === 1 ? '' : 's'}` : '';
      const who = match.person_type === 'candidate' ? ', candidates' : match.person_type === 'contact' ? ', contacts' : '';
      return `Meeting booked${where}${who}`;
    }
    case 'stage_change': {
      const to = match.to_stage ? `→ ${match.to_stage}` : '';
      const from = match.from_stage ? `${match.from_stage} ` : '';
      return `Stage change ${from}${to}`.trim() || 'Any stage change';
    }
    case 'list_added':
      return match.list_id ? `Added to list (${match.list_id.slice(0, 8)}…)` : 'Added to a JT list';
    case 'new_placement': {
      const type = EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === placementOptionValue(match.employment_type));
      return type ? `New ${type.label.toLowerCase()} placement` : 'Any new placement';
    }
  }
}

function TriggerNode({
  campaignName,
  audience,
  triggerConfig,
  selected,
  onSelect,
}: {
  campaignName: string;
  audience?: { id: string; name: string; member_count: number; is_dynamic: boolean } | null;
  triggerConfig: TriggerConfig | null;
  selected: boolean;
  onSelect: () => void;
}) {
  const triggerLabel = triggerConfig ? TRIGGER_KIND_LABEL[triggerConfig.type] : 'On launch';
  const triggerDetail = describeTrigger(triggerConfig);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        width: 'calc(var(--workflow-node-width) + var(--space-8))',
        borderRadius: 'var(--workflow-node-radius)',
        border: `1.5px solid ${selected ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
        borderLeft: `4px solid var(--color-brand-indigo)`,
        boxShadow: selected ? 'var(--shadow-focus)' : 'var(--shadow-sm)',
        backgroundColor: 'var(--color-surface-white)',
        padding: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'var(--space-8)',
            height: 'var(--space-8)',
            borderRadius: 'var(--radius-input)',
            backgroundColor: 'var(--color-indigo-50)',
            color: 'var(--color-brand-indigo)',
            border: '1px solid var(--color-indigo-100)',
          }}
        >
          <IconBolt size={16} />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-brand-indigo)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
            Trigger · {triggerLabel}
          </span>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
            {campaignName}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-surface-muted)', border: '1px solid var(--color-border-default)' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          {triggerConfig ? 'Auto-enroll' : 'Audience'}
        </span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
          {triggerConfig ? triggerDetail : audience ? `${audience.name} · ${audience.member_count} members` : 'Not set'}
        </span>
      </div>
    </button>
  );
}

/* ── Empty state ── */

function EmptyPrompt({ onAdd, disabled }: { onAdd: (t: StepType) => void; disabled: boolean }) {
  return (
    <>
      <ArrowConnector />
      <div
        style={{
          width: 'calc(var(--workflow-node-width) + var(--space-12))',
          borderRadius: 'var(--workflow-node-radius)',
          border: '2px dashed var(--color-border-strong)',
          backgroundColor: 'var(--color-surface-white)',
          padding: 'var(--space-6)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-3)',
          textAlign: 'center',
        }}
      >
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
          Add your first step
        </span>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', justifyContent: 'center' }}>
          {(['EMAIL', 'DELAY', 'BRANCH', 'TASK'] as StepType[]).map((t) => (
            <InlineTypePill key={t} type={t} disabled={disabled} onClick={() => onAdd(t)}>
              {STEP_TYPE_LABEL[t]}
            </InlineTypePill>
          ))}
        </div>
      </div>
    </>
  );
}

function InlineTypePill({ type, children, disabled, onClick }: { type: StepType; children: ReactNode; disabled?: boolean; onClick: () => void }) {
  const accent = TYPE_ACCENTS[type];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        height: 'var(--btn-height-sm)',
        padding: '0 var(--space-3)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: accent.bg,
        border: `1px solid ${accent.border}`,
        color: accent.fg,
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'transform var(--transition-base)',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <StepIcon type={type} size={12} />
      {children}
    </button>
  );
}

/* ── Flow renderer ── */

function FlowRenderer({ steps, selectedIdx, onSelect }: { steps: SequenceStep[]; selectedIdx: number | null; onSelect: (i: number) => void }) {
  return (
    <>
      {steps.map((step, idx) => (
        <NodeBlock key={idx} step={step} idx={idx} selected={idx === selectedIdx} onSelect={() => onSelect(idx)} />
      ))}
    </>
  );
}

function NodeBlock({ step, idx, selected, onSelect }: { step: SequenceStep; idx: number; selected: boolean; onSelect: () => void }) {
  return (
    <>
      <ArrowConnector />
      <NodeCard step={step} idx={idx} selected={selected} onSelect={onSelect} />
    </>
  );
}

function NodeCard({ step, idx, selected, onSelect }: { step: SequenceStep; idx: number; selected: boolean; onSelect: () => void }) {
  const accent = TYPE_ACCENTS[step.type];
  const description = describeStep(step);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        width: 'var(--workflow-node-width)',
        borderRadius: 'var(--workflow-node-radius)',
        border: `1.5px solid ${selected ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
        boxShadow: selected ? 'var(--shadow-focus)' : 'var(--shadow-sm)',
        backgroundColor: 'var(--color-surface-white)',
        padding: 'var(--workflow-node-padding)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'var(--space-8)',
            height: 'var(--space-8)',
            borderRadius: 'var(--radius-input)',
            backgroundColor: accent.bg,
            border: `1px solid ${accent.border}`,
            color: accent.fg,
            flexShrink: 0,
          }}
        >
          <StepIcon type={step.type} size={14} />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-bold)', color: accent.fg, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
            Step {idx + 1} · {STEP_TYPE_LABEL[step.type]}
          </span>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {labelForStep(step)}
          </span>
        </div>
      </div>
      <span
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-muted)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {description}
      </span>
    </button>
  );
}

function labelForStep(step: SequenceStep): string {
  if (step.type === 'EMAIL') return step.subject?.trim() || '(no subject)';
  if (step.type === 'DELAY') return `Wait ${step.delay_days ?? 0} day${step.delay_days === 1 ? '' : 's'}`;
  if (step.type === 'BRANCH') {
    const cond = (step.branch_condition as BranchCondition | undefined)?.type;
    return cond ? `Branch on ${cond}` : 'Branch (not configured)';
  }
  if (step.type === 'TASK') return step.task_title?.trim() || 'Recruiter task';
  return 'Exit sequence';
}

function describeStep(step: SequenceStep): string {
  if (step.type === 'EMAIL') {
    if (step.template_id) return 'Uses a template: open to edit.';
    const body = (step.body_text ?? '').trim();
    return body ? body.slice(0, 80) + (body.length > 80 ? '…' : '') : 'No body yet.';
  }
  if (step.type === 'DELAY') return 'Business days · respects send window.';
  if (step.type === 'BRANCH') {
    const bc = step.branch_condition as BranchCondition | undefined;
    if (!bc?.on_true && !bc?.on_false) return 'Routing not configured.';
    return `Yes → step ${bc.on_true ?? '?'} · No → step ${bc.on_false ?? '?'}`;
  }
  if (step.type === 'TASK') {
    const due = step.task_due_days ?? 2;
    return `Creates a JT task · due in ${due} business ${due === 1 ? 'day' : 'days'}`;
  }
  return '30-day cool-down before re-enrol.';
}

function StepIcon({ type, size = 14 }: { type: StepType; size?: number }) {
  switch (type) {
    case 'EMAIL': return <IconMail size={size} />;
    case 'DELAY': return <IconClock size={size} />;
    case 'BRANCH': return <IconBranch size={size} />;
    case 'TASK': return <IconBell size={size} />;
    case 'EXIT': return <IconSquare size={size} />;
  }
}

function ArrowConnector() {
  return (
    <div
      aria-hidden="true"
      style={{
        height: 'var(--space-8)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span style={{ width: '1.5px', height: 'calc(var(--space-8) - 8px)', backgroundColor: 'var(--workflow-connector-color)' }} />
      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ marginTop: '-1px' }}>
        <path d="M0 0 L5 6 L10 0" stroke="var(--workflow-connector-color)" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

/* ── Trigger config panel ── */

function TriggerConfigPanel({
  triggerConfig,
  readOnly,
  saving,
  onSave,
  onClose,
}: {
  triggerConfig: TriggerConfig | null;
  readOnly: boolean;
  saving: boolean;
  onSave: (cfg: TriggerConfig | null) => void;
  onClose: () => void;
}) {
  // Local draft state so the user can stage changes before pressing Save.
  // Initialised from props and resets if the underlying config changes
  // (e.g. another tab edited it).
  const [draft, setDraft] = useState<TriggerConfig | null>(triggerConfig);
  useEffect(() => { setDraft(triggerConfig); }, [triggerConfig]);

  // Lists are scoped by entity_type; without an audience link we don't
  // know whether the campaign targets candidates or contacts, so offer both.
  const allLists = useAllAvailableLists();

  const dirty = JSON.stringify(draft) !== JSON.stringify(triggerConfig);

  const handleKindChange = (kind: '' | TriggerKind) => {
    if (!kind) {
      setDraft(null);
      return;
    }
    // Switching kinds nukes match criteria from the previous kind — they
    // don't translate (a `to_stage` value is meaningless on a list trigger).
    setDraft({ type: kind, match: {} });
  };

  const patchMatch = (patch: Record<string, string | undefined>) => {
    if (!draft) return;
    const nextMatch: TriggerConfig['match'] = { ...draft.match };
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') {
        delete (nextMatch as Record<string, unknown>)[k];
      } else {
        (nextMatch as Record<string, unknown>)[k] = v;
      }
    }
    setDraft({ ...draft, match: nextMatch });
  };

  return (
    <aside
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'sticky',
        top: 'var(--space-4)',
        maxHeight: 'calc(100vh - var(--space-12))',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          padding: 'var(--space-4) var(--space-5)',
          borderBottom: '1px solid var(--color-border-default)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'var(--space-8)',
              height: 'var(--space-8)',
              borderRadius: 'var(--radius-input)',
              backgroundColor: 'var(--color-indigo-50)',
              border: '1px solid var(--color-indigo-100)',
              color: 'var(--color-brand-indigo)',
            }}
          >
            <IconBolt size={14} />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-brand-indigo)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
              Trigger
            </span>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)' }}>
              Auto-enrollment
            </span>
          </div>
        </div>
        <button
          type="button"
          aria-label="Close panel"
          onClick={onClose}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-input)',
            border: 'none',
            backgroundColor: 'transparent',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
          }}
        >
          <IconX size={14} />
        </button>
      </header>

      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-relaxed)' }}>
          Triggers enroll contacts automatically as JT data changes. Leave on
          <strong style={{ color: 'var(--color-text-primary)' }}> On launch </strong>
          to only enroll the static audience snapshot when the campaign starts.
        </p>

        <Field label="Trigger type">
          <Select
            value={draft?.type ?? ''}
            disabled={readOnly}
            onChange={(value) => handleKindChange(value as '' | TriggerKind)}
          >
            <option value="">On launch (no auto-enrollment)</option>
            {/* The legacy typed-stage trigger matched the candidate's linked/unlinked type, never a
                pipeline stage, so it could not fire (JT1-1917). Only shown for campaigns that still have it. */}
            {draft?.type === 'stage_change' && <option value="stage_change">Old stage change (no longer works)</option>}
            <option value="list_added">Added to a list</option>
            <option value="new_placement">New placement</option>
            <option value="meeting_booked">Meeting booked</option>
          </Select>
        </Field>

        {draft?.type === 'stage_change' && (
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-relaxed)' }}>
            This trigger can't start the campaign. Pick another trigger, or use the campaign's Enrollment step to
            start it from a job pipeline stage.
          </p>
        )}

        {draft?.type === 'list_added' && (
          <Field
            label="When added to list"
            hint={allLists.length === 0 ? 'No JT lists synced yet. Run a sync from Settings.' : 'Pick the JT list to watch.'}
          >
            <Select
              value={draft.match?.list_id ?? ''}
              disabled={readOnly || allLists.length === 0}
              onChange={(value) => patchMatch({ list_id: value })}
            >
              <option value="">Pick a list…</option>
              {allLists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.entity_type})
                </option>
              ))}
            </Select>
          </Field>
        )}

        {draft?.type === 'new_placement' && (
          <>
            <Field label="Placement type" hint="Fires when a placement is created. Optional: only fire for placements of this type.">
              <Select
                value={placementOptionValue(draft.match?.employment_type)}
                disabled={readOnly}
                onChange={(value) => patchMatch({ employment_type: value, status: undefined })}
              >
                <option value="">Any type</option>
                {EMPLOYMENT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
          </>
        )}

        {draft?.type === 'meeting_booked' && (
          <MeetingBookedFields
            eventTypeIds={draft.match?.event_type_ids ?? []}
            personType={draft.match?.person_type ?? 'any'}
            disabled={readOnly}
            onChange={(next) => {
              const nextMatch: NonNullable<TriggerConfig['match']> = { ...draft.match, person_type: next.person_type };
              if (next.event_type_ids.length) nextMatch.event_type_ids = next.event_type_ids;
              else delete nextMatch.event_type_ids;
              setDraft({ ...draft, match: nextMatch });
            }}
          />
        )}

        {draft && (
          <div
            style={{
              padding: 'var(--space-3)',
              border: '1px solid var(--color-border-default)',
              borderRadius: 'var(--radius-input)',
              backgroundColor: 'var(--color-surface-muted)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
              lineHeight: 'var(--leading-relaxed)',
            }}
          >
            <strong style={{ color: 'var(--color-text-primary)' }}>Preview:</strong> {describeTrigger(draft)}.
            <br />
            Each matching event will enroll the contact once. Existing enrollments are skipped.
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 'var(--space-2)',
          padding: 'var(--space-4) var(--space-5)',
          borderTop: '1px solid var(--color-border-default)',
          backgroundColor: 'var(--color-surface-muted)',
        }}
      >
        <PrimaryButton onClick={() => onSave(draft)} disabled={readOnly || !dirty || saving}>
          {saving ? 'Saving…' : 'Save trigger'}
        </PrimaryButton>
      </div>
    </aside>
  );
}

/* ── Right config panel ── */

function ConfigPanel({
  step,
  stepIdx,
  allSteps,
  templates,
  readOnly,
  onChange,
  onRemove,
  onClose,
}: {
  step: SequenceStep;
  stepIdx: number;
  allSteps: SequenceStep[];
  templates: Template[];
  readOnly: boolean;
  onChange: (patch: Partial<SequenceStep>) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  return (
    <aside
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'sticky',
        top: 'var(--space-4)',
        maxHeight: 'calc(100vh - var(--space-12))',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          padding: 'var(--space-4) var(--space-5)',
          borderBottom: '1px solid var(--color-border-default)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'var(--space-8)',
              height: 'var(--space-8)',
              borderRadius: 'var(--radius-input)',
              backgroundColor: TYPE_ACCENTS[step.type].bg,
              border: `1px solid ${TYPE_ACCENTS[step.type].border}`,
              color: TYPE_ACCENTS[step.type].fg,
            }}
          >
            <StepIcon type={step.type} size={14} />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-bold)', color: TYPE_ACCENTS[step.type].fg, textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
              Step {stepIdx + 1} · {STEP_TYPE_LABEL[step.type]}
            </span>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)' }}>
              Configure
            </span>
          </div>
        </div>
        <button
          type="button"
          aria-label="Close panel"
          onClick={onClose}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-input)',
            border: 'none',
            backgroundColor: 'transparent',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
          }}
        >
          <IconX size={14} />
        </button>
      </header>

      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Field label="Step type">
          <Select
            value={step.type}
            disabled={readOnly}
            onChange={(value) => onChange({ type: value as StepType })}
          >
            {(['EMAIL', 'DELAY', 'BRANCH', 'TASK', 'EXIT'] as StepType[]).map((t) => (
              <option key={t} value={t}>{STEP_TYPE_LABEL[t]}</option>
            ))}
          </Select>
        </Field>

        {step.type === 'EMAIL' && <EmailPanel step={step} templates={templates} readOnly={readOnly} onChange={onChange} />}
        {step.type === 'DELAY' && <DelayPanel step={step} readOnly={readOnly} onChange={onChange} />}
        {step.type === 'BRANCH' && <BranchPanel step={step} stepIdx={stepIdx} allSteps={allSteps} readOnly={readOnly} onChange={onChange} />}
        {step.type === 'TASK' && <TaskPanel step={step} readOnly={readOnly} onChange={onChange} />}
        {step.type === 'EXIT' && <ExitPanel />}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 'var(--space-2)',
          padding: 'var(--space-4) var(--space-5)',
          borderTop: '1px solid var(--color-border-default)',
          backgroundColor: 'var(--color-surface-muted)',
        }}
      >
        <DangerButton onClick={onRemove} disabled={readOnly}>
          <IconTrash size={12} /><span style={{ marginLeft: 'var(--space-1)' }}>Remove step</span>
        </DangerButton>
      </div>
    </aside>
  );
}

function EmailPanel({ step, templates, readOnly, onChange }: { step: SequenceStep; templates: Template[]; readOnly: boolean; onChange: (p: Partial<SequenceStep>) => void }) {
  const { addToast } = useToast();
  const draft = useLinkiDraft();
  const rewrite = useLinkiRewrite();
  const [linkiOpen, setLinkiOpen] = useState(false);
  const [linkiPrompt, setLinkiPrompt] = useState('');
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [rewriteInstruction, setRewriteInstruction] = useState('');

  const handleTemplate = (tplId: string) => {
    const tpl = templates.find((t) => t.id === tplId);
    if (!tplId) {
      onChange({ template_id: null });
      return;
    }
    onChange({
      template_id: tplId,
      ...(tpl ? { subject: tpl.subject, body_html: tpl.body_html, body_text: tpl.body_text } : {}),
    });
  };

  const runDraft = async () => {
    if (!linkiPrompt.trim()) return;
    try {
      const result = await draft.mutateAsync({
        prompt: linkiPrompt.trim(),
        context: { current_subject: step.subject ?? '' },
        // step_type maps to the prompt template Linki uses to choose
        // initial-outreach vs follow-up tone. Use step_number as a heuristic
        // until we expose explicit step intent in the UI.
        step_type: step.step_number === 1 ? 'initial_outreach' : 'follow_up',
      });
      onChange({
        body_text: result.text,
        body_html: result.text.replace(/\n/g, '<br/>'),
      });
      setLinkiOpen(false);
      setLinkiPrompt('');
      addToast({ variant: 'success', title: 'Linki drafted a body' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Try again in a moment.';
      addToast({ variant: 'danger', title: 'Draft failed', description: msg });
    }
  };

  const runRewrite = async () => {
    if (!rewriteInstruction.trim() || !(step.body_text ?? '').trim()) return;
    try {
      const result = await rewrite.mutateAsync({
        current_text: step.body_text ?? '',
        instruction: rewriteInstruction.trim(),
      });
      onChange({
        body_text: result.text,
        body_html: result.text.replace(/\n/g, '<br/>'),
      });
      setRewriteOpen(false);
      setRewriteInstruction('');
      addToast({ variant: 'success', title: 'Linki rewrote the body' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Try again in a moment.';
      addToast({ variant: 'danger', title: 'Rewrite failed', description: msg });
    }
  };

  const hasBody = !!(step.body_text ?? '').trim();

  return (
    <>
      {templates.length > 0 && (
        <Field label="Template" hint="Picking a template fills the subject + body below.">
          <Select value={step.template_id ?? ''} disabled={readOnly} onChange={handleTemplate}>
            <option value="">Custom (no template)</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </Select>
        </Field>
      )}

      <Field label="Subject line" hint="Click { } to insert a personalisation tag.">
        <TokenField
          label="Subject line"
          value={step.subject ?? ''}
          disabled={readOnly}
          tokens={COMMON_MERGE_TOKENS}
          placeholder="Quick intro re: {{company.name}}"
          onChange={(next) => onChange({ subject: next })}
          style={textInputStyle(readOnly)}
        />
      </Field>

      <Field label="Body">
        <TokenField
          label="Body"
          multiline
          rows={8}
          value={step.body_text ?? ''}
          disabled={readOnly}
          tokens={COMMON_MERGE_TOKENS}
          placeholder="Hi {{first_name}}, …"
          onChange={(next) => onChange({ body_text: next, body_html: next.replace(/\n/g, '<br/>') })}
          style={{
            ...textInputStyle(readOnly),
            height: 'auto',
            padding: 'var(--space-3)',
            resize: 'vertical',
            lineHeight: 'var(--leading-normal)',
          }}
        />
      </Field>

      {!readOnly && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            <LinkiButton onClick={() => { setLinkiOpen((v) => !v); setRewriteOpen(false); }} active={linkiOpen}>
              Draft with Linki
            </LinkiButton>
            {hasBody && (
              <LinkiButton onClick={() => { setRewriteOpen((v) => !v); setLinkiOpen(false); }} active={rewriteOpen}>
                Rewrite with Linki
              </LinkiButton>
            )}
          </div>

          {linkiOpen && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                padding: 'var(--space-3)',
                border: '1px solid var(--color-linki-border)',
                borderRadius: 'var(--radius-input)',
                backgroundColor: 'var(--color-linki-bg)',
              }}
            >
              <textarea
                value={linkiPrompt}
                onChange={(e) => setLinkiPrompt(e.target.value)}
                rows={3}
                placeholder="What should this email say? e.g. 'Quick intro about a senior backend role in Sydney'"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: 'var(--space-2)',
                  border: '1px solid var(--input-border)',
                  borderRadius: 'var(--input-radius)',
                  backgroundColor: 'var(--input-bg)',
                  fontSize: 'var(--input-font-size)',
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--color-text-primary)',
                  outline: 'none',
                  resize: 'vertical',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
                <SmallSecondaryButton onClick={() => { setLinkiOpen(false); setLinkiPrompt(''); }}>
                  Cancel
                </SmallSecondaryButton>
                <SmallPrimaryButton onClick={runDraft} disabled={!linkiPrompt.trim() || draft.isPending}>
                  {draft.isPending ? 'Generating…' : 'Generate'}
                </SmallPrimaryButton>
              </div>
            </div>
          )}

          {rewriteOpen && hasBody && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                padding: 'var(--space-3)',
                border: '1px solid var(--color-linki-border)',
                borderRadius: 'var(--radius-input)',
                backgroundColor: 'var(--color-linki-bg)',
              }}
            >
              <input
                type="text"
                value={rewriteInstruction}
                onChange={(e) => setRewriteInstruction(e.target.value)}
                placeholder="Make it shorter, more casual, etc."
                style={{
                  display: 'block',
                  width: '100%',
                  height: 'var(--input-height)',
                  padding: '0 var(--space-3)',
                  border: '1px solid var(--input-border)',
                  borderRadius: 'var(--input-radius)',
                  backgroundColor: 'var(--input-bg)',
                  fontSize: 'var(--input-font-size)',
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--color-text-primary)',
                  outline: 'none',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
                <SmallSecondaryButton onClick={() => { setRewriteOpen(false); setRewriteInstruction(''); }}>
                  Cancel
                </SmallSecondaryButton>
                <SmallPrimaryButton onClick={runRewrite} disabled={!rewriteInstruction.trim() || rewrite.isPending}>
                  {rewrite.isPending ? 'Rewriting…' : 'Apply'}
                </SmallPrimaryButton>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function LinkiButton({ children, onClick, active }: { children: ReactNode; onClick: () => void; active: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        height: 'var(--btn-height-sm)',
        padding: '0 var(--btn-padding-x-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: active ? 'var(--color-brand-teal)' : 'var(--color-linki-bg)',
        color: active ? 'var(--color-text-inverse)' : 'var(--color-linki-text)',
        border: `1px solid ${active ? 'var(--color-brand-teal)' : 'var(--color-linki-border)'}`,
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        cursor: 'pointer',
      }}
    >
      <IconSparkSmall size={11} />
      {children}
    </button>
  );
}

function SmallPrimaryButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 'var(--btn-height-sm)',
        padding: '0 var(--btn-padding-x-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-primary)',
        color: 'var(--color-action-primary-text)',
        border: 'none',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function SmallSecondaryButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 'var(--btn-height-sm)',
        padding: '0 var(--btn-padding-x-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-secondary)',
        color: 'var(--color-action-secondary-text)',
        border: '1px solid var(--color-action-secondary-border)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function IconSparkSmall({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l2.39 6.96L21 11l-6.61 2.04L12 20l-2.39-6.96L3 11l6.61-2.04L12 2z" />
    </svg>
  );
}

function DelayPanel({ step, readOnly, onChange }: { step: SequenceStep; readOnly: boolean; onChange: (p: Partial<SequenceStep>) => void }) {
  return (
    <Field label="Wait" hint="Business days. Respects the campaign's send window.">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <div style={{ width: '120px' }}>
          <TextInput
            type="number"
            min={0}
            max={60}
            value={step.delay_days ?? 0}
            disabled={readOnly}
            onChange={(e) => onChange({ delay_days: parseInt(e.target.value, 10) || 0 })}
          />
        </div>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          business {step.delay_days === 1 ? 'day' : 'days'}
        </span>
      </div>
    </Field>
  );
}

function BranchPanel({
  step,
  stepIdx,
  allSteps,
  readOnly,
  onChange,
}: {
  step: SequenceStep;
  stepIdx: number;
  allSteps: SequenceStep[];
  readOnly: boolean;
  onChange: (p: Partial<SequenceStep>) => void;
}) {
  const bc = (step.branch_condition as BranchCondition | undefined) ?? {};
  // Only steps AFTER this one are valid jump targets — anything earlier
  // would create a loop, which the workflow engine doesn't yet support.
  const downstreamOptions = useMemo(() => {
    const opts: { value: number; label: string }[] = [];
    for (let i = stepIdx + 1; i < allSteps.length; i++) {
      opts.push({ value: i + 1, label: `Step ${i + 1} · ${STEP_TYPE_LABEL[allSteps[i].type]}` });
    }
    return opts;
  }, [allSteps, stepIdx]);

  const patchBranch = (patch: Partial<BranchCondition>) => {
    onChange({ branch_condition: { ...bc, ...patch } as never });
  };

  return (
    <>
      <Field label="Condition">
        <Select
          value={bc.type ?? ''}
          disabled={readOnly}
          onChange={(value) => patchBranch({ type: (value || undefined) as BranchCondition['type'] })}
        >
          <option value="">Pick a condition…</option>
          {BRANCH_CONDITIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </Select>
      </Field>

      <Field label="If YES, jump to" hint={downstreamOptions.length === 0 ? 'Add later steps to enable routing.' : undefined}>
        <Select
          value={bc.on_true?.toString() ?? ''}
          disabled={readOnly || downstreamOptions.length === 0}
          onChange={(value) => patchBranch({ on_true: value ? parseInt(value, 10) : null })}
        >
          <option value="">Fall through</option>
          {downstreamOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
      </Field>

      <Field label="If NO, jump to">
        <Select
          value={bc.on_false?.toString() ?? ''}
          disabled={readOnly || downstreamOptions.length === 0}
          onChange={(value) => patchBranch({ on_false: value ? parseInt(value, 10) : null })}
        >
          <option value="">Fall through</option>
          {downstreamOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
      </Field>
    </>
  );
}

function TaskPanel({ step, readOnly, onChange }: { step: SequenceStep; readOnly: boolean; onChange: (p: Partial<SequenceStep>) => void }) {
  const { data: orgUsers } = useOrgUsers();
  return (
    <>
      <Field label="Task title" hint="Click { } to insert a personalisation tag.">
        <TokenField
          label="Task title"
          value={step.task_title ?? ''}
          disabled={readOnly}
          tokens={TASK_MERGE_TOKENS}
          placeholder="Call {{first_name}} {{last_name}} from {{company}}"
          onChange={(next) => onChange({ task_title: next })}
          style={textInputStyle(readOnly)}
        />
      </Field>

      <Field label="Assignee" hint="The task goes to this person in JobTetris.">
        <TaskAssigneeSelect
          value={step.task_assignee_id}
          disabled={readOnly}
          users={orgUsers}
          onChange={(next) => onChange({ task_assignee_id: next })}
          style={textInputStyle(readOnly)}
        />
      </Field>

      <Field label="Due in" hint="Business days from when the task is created.">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <div style={{ width: '120px' }}>
            <TextInput
              type="number"
              min={0}
              max={60}
              value={step.task_due_days ?? 2}
              disabled={readOnly}
              onChange={(e) => onChange({ task_due_days: parseInt(e.target.value, 10) || 0 })}
            />
          </div>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            business {step.task_due_days === 1 ? 'day' : 'days'}
          </span>
        </div>
      </Field>

      <div
        style={{
          padding: 'var(--space-3)',
          border: '1px solid var(--color-border-default)',
          borderRadius: 'var(--radius-input)',
          backgroundColor: 'var(--color-surface-muted)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-muted)',
          lineHeight: 'var(--leading-relaxed)',
        }}
      >
        When this step fires, Engage creates a JobTetris task for the assignee,
        linked to the candidate or contact. If JobTetris can't create it, the campaign
        page says why, and the sequence carries on.
      </div>
    </>
  );
}

function ExitPanel() {
  return (
    <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
      Ends the sequence for any enrollment that reaches this step. The
      enrollment is marked COMPLETED and won't receive further messages.
    </p>
  );
}

/* ── Form primitives ── */

function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>
        {label}
      </span>
      {children}
      {hint && (
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{hint}</span>
      )}
    </div>
  );
}

function textInputStyle(disabled?: boolean): React.CSSProperties {
  return {
    display: 'block',
    width: '100%',
    height: 'var(--input-height)',
    padding: 'var(--input-padding-y) var(--input-padding-x)',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--input-radius)',
    backgroundColor: disabled ? 'var(--color-surface-muted)' : 'var(--input-bg)',
    fontSize: 'var(--input-font-size)',
    fontFamily: 'var(--font-sans)',
    color: 'var(--color-text-primary)',
    outline: 'none',
  };
}

function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} style={{ ...textInputStyle(props.disabled), ...(props.style ?? {}) }} />;
}

function Select({
  children,
  value,
  onChange,
  disabled,
}: {
  children: ReactNode;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        style={{
          display: 'block',
          width: '100%',
          height: 'var(--input-height)',
          padding: 'var(--input-padding-y) var(--space-8) var(--input-padding-y) var(--input-padding-x)',
          border: '1px solid var(--input-border)',
          borderRadius: 'var(--input-radius)',
          backgroundColor: disabled ? 'var(--color-surface-muted)' : 'var(--input-bg)',
          fontSize: 'var(--input-font-size)',
          fontFamily: 'var(--font-sans)',
          color: 'var(--color-text-primary)',
          outline: 'none',
          appearance: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {children}
      </select>
      <span aria-hidden="true" style={{ position: 'absolute', right: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--color-text-muted)' }}>
        <IconChevronDown size={14} />
      </span>
    </div>
  );
}

/* ── Buttons ── */

function PrimaryButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-primary)',
        color: 'var(--color-action-primary-text)',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color var(--transition-base)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function DangerButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-surface-white)',
        color: 'var(--color-action-danger)',
        border: '1px solid var(--color-danger-border)',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function LoadingShell() {
  return (
    <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
      Loading campaign…
    </div>
  );
}

/* ── Icons ── */

function IconChevronRight({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>; }
function IconChevronDown({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>; }
function IconPlus({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function IconMinus({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function IconX({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function IconSave({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>; }
function IconTrash({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>; }
function IconBolt({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 11 14 9 22 19 10 13 10 13 2" /></svg>; }
function IconMail({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22 6 12 13 2 6" /></svg>; }
function IconClock({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>; }
function IconBranch({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="3" x2="6" y2="15" /><circle cx="18" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" /></svg>; }
function IconBell({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>; }
function IconSquare({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2" /></svg>; }
